// ─── Multi-sheet persistence (IndexedDB) ──────────────────────────────────────
// Sheets (with their photos) are stored in IndexedDB, which has room for
// hundreds of MB — localStorage only holds ~5MB, and once photos filled it,
// saves silently stopped. All sheets are loaded into memory at startup
// (initStorage) so reads stay synchronous; writes go to IndexedDB in the
// background. If a write fails, listeners registered with onSaveStatus are told
// so the app can warn the tech instead of failing silently.
const SAVE_PREFIX = "waimea_sheet_";
const SAVE_INDEX  = "waimea_sheet_index";
const DARK_KEY    = "waimea_dark_mode";
const DB_NAME = "waimea_checksheets", STORE = "sheets";

const cache = new Map();          // key -> sheet data
let dbPromise = null;
const listeners = new Set();
let lastOk = true;

// Some devices never finish opening IndexedDB; give up after 4s so the app
// still starts (the save warning shows) instead of sitting on a blank screen.
// A failed open is retried on the next save.
function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Sheet storage didn't open")), 4000);
      let req;
      try { req = indexedDB.open(DB_NAME, 1); } catch(e) { clearTimeout(timer); return reject(e); }
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => { clearTimeout(timer); resolve(req.result); };
      req.onerror = () => { clearTimeout(timer); reject(req.error); };
      req.onblocked = () => { clearTimeout(timer); reject(new Error("Sheet storage is blocked by another tab")); };
    });
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
}
function tx(mode, fn) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const out = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(out && out.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error("Save aborted"));
  }));
}
function report(ok, error) {
  lastOk = ok;
  listeners.forEach(fn => { try { fn({ ok, error }); } catch(e) {} });
}
export function onSaveStatus(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function lastSaveOk() { return lastOk; }

// Load every sheet into memory, moving any old localStorage sheets across.
// Also asks the browser not to evict our data when the device is low on space.
export async function initStorage() {
  try { if (navigator.storage && navigator.storage.persist) navigator.storage.persist().catch(() => {}); } catch(e) {}
  try {
    const all = await tx("readonly", s => s.getAll());
    const keys = await tx("readonly", s => s.getAllKeys());
    (keys || []).forEach((k, i) => cache.set(k, all[i]));
  } catch(e) { console.warn("Couldn't open sheet storage:", e); report(false, e); return; }  // leave old sheets in localStorage for next time
  // One-off migration from localStorage (pre-v53)
  try {
    const index = JSON.parse(localStorage.getItem(SAVE_INDEX) || "[]");
    for (const key of index) {
      try {
        const sheet = JSON.parse(localStorage.getItem(SAVE_PREFIX + key));
        if (sheet && !cache.has(key)) { cache.set(key, sheet); await tx("readwrite", s => s.put(sheet, key)); }
        localStorage.removeItem(SAVE_PREFIX + key);
      } catch(e) { console.warn("Couldn't migrate sheet", key, e); }
    }
    if (index.length) localStorage.removeItem(SAVE_INDEX);
  } catch(e) {}
}

export function getAllSheets() {
  return [...cache.values()].filter(Boolean).sort((a,b) => new Date(b.savedAt) - new Date(a.savedAt));
}

// Writes are coalesced per sheet: while one write is in flight, only the newest
// pending version is written next. Returns a promise of true/false.
const pending = new Map(), writing = new Set(), waiters = new Map();
function flush(key) {
  if (writing.has(key) || !pending.has(key)) return;
  const data = pending.get(key); pending.delete(key); writing.add(key);
  tx("readwrite", s => s.put(data, key))
    .then(() => {
      writing.delete(key);
      if (!cache.has(key)) tx("readwrite", s => s.delete(key)).catch(() => {});  // deleted while writing
      if (!pending.has(key)) { report(true); settle(key, true); }
      flush(key);
    })
    .catch(e => { writing.delete(key); console.warn("Save failed:", e); report(false, e); settle(key, false); flush(key); });
}
function settle(key, ok) { (waiters.get(key) || []).forEach(r => r(ok)); waiters.delete(key); }

// Wait (up to maxMs) for every queued or in-flight save to finish — used before
// reloading for an update. Resolves true if they all saved.
export function flushSaves(maxMs = 3000) {
  const keys = [...new Set([...pending.keys(), ...writing])];
  if (!keys.length) return Promise.resolve(true);
  const all = Promise.all(keys.map(k => new Promise(r => waiters.set(k, [...(waiters.get(k) || []), r]))))
    .then(results => results.every(Boolean));
  return Promise.race([all, new Promise(r => setTimeout(() => r(false), maxMs))]);
}

export function saveSheet(jobno, data) {
  const key = jobno || "__draft__";
  const sheet = { ...data, jobno: key, savedAt: new Date().toISOString() };
  cache.set(key, sheet);
  pending.set(key, sheet);
  const done = new Promise(r => waiters.set(key, [...(waiters.get(key) || []), r]));
  flush(key);
  return done;
}

export function deleteSheet(jobno) {
  const key = jobno || "__draft__";
  cache.delete(key);
  pending.delete(key);
  tx("readwrite", s => s.delete(key)).catch(e => console.warn("Delete failed:", e));
}

export function getDarkMode() {
  try { return localStorage.getItem(DARK_KEY) === "true"; }
  catch(e) { return false; }
}

// Android: also save each camera photo to the device (on unless switched off)
const AUTOSAVE_KEY = "waimea_autosave_photos";
export function getAutoSavePhotos() {
  try { return localStorage.getItem(AUTOSAVE_KEY) !== "false"; }
  catch(e) { return true; }
}
export function setAutoSavePhotos(val) {
  try { localStorage.setItem(AUTOSAVE_KEY, val ? "true" : "false"); }
  catch(e) {}
}

export function setDarkMode(val) {
  try { localStorage.setItem(DARK_KEY, val ? "true" : "false"); }
  catch(e) {}
}
